import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import AddIcon from '@mui/icons-material/Add';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import HotelOutlinedIcon from '@mui/icons-material/HotelOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import ReplayIcon from '@mui/icons-material/Replay';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    IconButton,
    Link,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import PageLoader from 'components/PageLoader';
import DrawerMenu from 'components/DrawerMenu';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import axiosInstance from 'utils/axios';
import {URLS} from 'utils/constants/urls';
import Currency from 'utils/hooks/getCurrency';
import {createDropdownData} from 'utils/validators/HelperFunctions';
import strings from '../../../../common/Translation/Translate';
import Config from '../../../../utils/Config';
import {privileges} from '../../../../utils/constants/privileges';
import {selectIntegration, User} from '../../../../utils/redux/reducer/authentication-slice';
import {AddPaymentFormPanel} from '../BookingDetailButtons/AddPayment';
import {AddReceivableButton} from '../BookingDetailButtons/AddReceivable';
import {getReceivableTypeLabel} from '../BookingDetailButtons/AddReceivable/receivableUtils';
import {SettleReceivableDrawer} from '../BookingDetailButtons/SettleReceivable';
import {ManageReceivableDrawer} from '../BookingDetailButtons/ManageReceivable';
import '../styles.scss';

dayjs.extend(advancedFormat);

interface PaymentDetailsProps {
    bookingLineItem: any;
    permission: any;
    onSuccess: () => void;
}

type PaymentRowType = 'EVENT' | 'BOOKED_ROOMS';

type PaymentCollectionStatus = 'PAID' | 'PARTIAL' | 'PENDING';

type SettlementEntryStatus = 'OPEN' | 'PARTIALLY_SETTLED' | 'SETTLED' | 'CANCELLED';

type PaymentDrawerContext = {
    rowId: string;
    itemId: number;
    isBookedRooms: boolean;
    eventName: string;
    venue: string;
    dateLabel: string;
    payments: any[];
    amountDue: number;
    amountPaid: number;
    itemStatus?: string;
};

const EVENT_TYPE_CHIP_SX = {backgroundColor: '#E8F0FE', color: '#1967D2', fontWeight: 600, fontSize: 11};
const BOOKED_ROOMS_TYPE_CHIP_SX = {backgroundColor: '#F3E8FD', color: '#7B1FA2', fontWeight: 600, fontSize: 11};
const REFUND_TYPE_CHIP_SX = {backgroundColor: '#FCE8E6', color: '#C5221F', fontWeight: 600, fontSize: 11};
const RECEIVABLE_TYPE_CHIP_SX = {backgroundColor: '#E6F4EA', color: '#137333', fontWeight: 600, fontSize: 11};

const getPaymentModeChipColor = () => ({backgroundColor: '#E8F0FE', color: '#1967D2'});

const drawerHeaderCellSx = {
    fontWeight: 600,
    color: '#666',
    textTransform: 'uppercase',
    fontSize: 11,
    whiteSpace: 'nowrap',
} as const;

const formatPaymentDate = (value: any) => {
    if (!value) return '—';
    return dayjs(value).format('DD MMM, YYYY');
};

const formatPaymentDateTimeCell = (value: any, isRefund = false) => {
    if (!value) return '—';
    const dateTime = dayjs(value);
    return (
        <Box>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', lineHeight: 1.35}}>
                <Typography variant="body2" sx={{lineHeight: 1.35, fontWeight: 500}}>
                    {dateTime.format('D MMM YYYY')}
                </Typography>
                {isRefund && (
                    <Chip label={strings.paymentDetails_type_refund} size="small" sx={REFUND_TYPE_CHIP_SX}/>
                )}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{lineHeight: 1.35, display: 'block'}}>
                {dateTime.format('h:mm A')}
            </Typography>
        </Box>
    );
};

const COMPLEMENTARY_CHIP_SX = { bgcolor: '#e0f7fa', color: '#00838f', fontWeight: 700 };

const getPaymentCollectionStatus = (total: number, paid: number, due: number): PaymentCollectionStatus => {
    const totalNum = Number(total) || 0;
    const paidNum = Number(paid) || 0;
    const dueNum = Number(due) || 0;

    if (totalNum <= 0 || dueNum <= 0) {
        return 'PAID';
    }
    if (paidNum > 0 && dueNum > 0) {
        return 'PARTIAL';
    }
    return 'PENDING';
};

const PaymentMetaDetails = ({payment}: {payment: any}) => {
    const showTds = payment?.isTdsDeducted === true;
    const roomNumber = payment?.roomNumber?.trim?.() || payment?.roomNumber;

    if (!showTds && !roomNumber) {
        return null;
    }

    return (
        <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.75}}>
            {showTds && (
                <Chip
                    label={strings.TDS_Deducted}
                    size="small"
                    sx={{height: 18, fontSize: 10, fontWeight: 600, bgcolor: '#FFF4E5', color: '#D17A00'}}
                />
            )}
            {roomNumber && (
                <Chip
                    label={`${strings.paymentDetails_room_number} ${roomNumber}`}
                    size="small"
                    sx={{height: 18, fontSize: 10, fontWeight: 600, bgcolor: '#F3E8FD', color: '#7B1FA2'}}
                />
            )}
        </Box>
    );
};

const PaymentStatusIndicator = ({status, isComplementary = false}: {status: PaymentCollectionStatus; isComplementary?: boolean}) => {
    if (isComplementary) {
        return (
            <Chip
                label={strings.bookingDetail_badge_complementary}
                size="small"
                sx={{height: 24, ...COMPLEMENTARY_CHIP_SX}}
            />
        );
    }

    switch (status) {
        case 'PAID':
            return (
                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, justifyContent: 'center'}}>
                    <CheckCircleOutlineIcon sx={{color: '#188038', fontSize: 20}}/>
                    <Typography variant="body2" sx={{color: '#188038', fontWeight: 600, fontSize: 12}}>
                        {strings.paymentDetails_status_paid}
                    </Typography>
                </Box>
            );
        case 'PARTIAL':
            return (
                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, justifyContent: 'center'}}>
                    <InfoOutlinedIcon sx={{color: '#D17A00', fontSize: 20}}/>
                    <Typography variant="body2" sx={{color: '#D17A00', fontWeight: 600, fontSize: 12}}>
                        {strings.paymentDetails_status_partiallyPaid}
                    </Typography>
                </Box>
            );
        default:
            return (
                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, justifyContent: 'center'}}>
                    <WarningAmberOutlinedIcon sx={{color: '#D93025', fontSize: 20}}/>
                    <Typography variant="body2" sx={{color: '#D93025', fontWeight: 600, fontSize: 12}}>
                        {strings.paymentDetails_status_pending}
                    </Typography>
                </Box>
            );
    }
};

const ReceivableStatusIndicator = ({status}: {status: SettlementEntryStatus}) => {
    switch (status) {
        case 'SETTLED':
            return (
                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, justifyContent: 'center'}}>
                    <CheckCircleOutlineIcon sx={{color: '#188038', fontSize: 20}}/>
                    <Typography variant="body2" sx={{color: '#188038', fontWeight: 600, fontSize: 12}}>
                        {strings.receivable_status_settled}
                    </Typography>
                </Box>
            );
        case 'PARTIALLY_SETTLED':
            return (
                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, justifyContent: 'center'}}>
                    <InfoOutlinedIcon sx={{color: '#D17A00', fontSize: 20}}/>
                    <Typography variant="body2" sx={{color: '#D17A00', fontWeight: 600, fontSize: 12}}>
                        {strings.receivable_status_partiallySettled}
                    </Typography>
                </Box>
            );
        case 'CANCELLED':
            return (
                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, justifyContent: 'center'}}>
                    <Typography variant="body2" sx={{color: '#666', fontWeight: 600, fontSize: 12}}>
                        {strings.receivable_status_cancelled}
                    </Typography>
                </Box>
            );
        default:
            return (
                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, justifyContent: 'center'}}>
                    <WarningAmberOutlinedIcon sx={{color: '#D93025', fontSize: 20}}/>
                    <Typography variant="body2" sx={{color: '#D93025', fontWeight: 600, fontSize: 12}}>
                        {strings.receivable_status_open}
                    </Typography>
                </Box>
            );
    }
};

const PaymentDetails = ({bookingLineItem, permission, onSuccess}: PaymentDetailsProps) => {
    const booking = bookingLineItem.booking;
    const status = bookingLineItem.status;
    const isComplementaryBooking = Boolean(
        booking?.isComplementary ?? bookingLineItem?.isComplementary ?? bookingLineItem?.complementary,
    );
    const integration = useSelector(selectIntegration);
    const user = useSelector(User) as {role?: {value?: string}} | null;
    const isBbrAdmin = user?.role?.value === 'ROLE_BBR_ADMIN';
    const isYcs = ((integration as string) || '').toUpperCase() === 'YCS';
    const theme = useTheme();
    const screenSizeDownSm = useMediaQuery(theme.breakpoints.down('sm'));

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [paymentDetails, setPaymentDetails] = useState<any>({});
    const [bookingLineItemPayments, setBookingLineItemPayments] = useState<any>([]);
    const [bookedRoomsPayments, setBookedRoomsPayments] = useState<any>({});
    const [settlementEntries, setSettlementEntries] = useState<any[]>([]);
    const [paymentMethods, setPaymentMethods] = useState<any>([]);
    const [drawerContext, setDrawerContext] = useState<PaymentDrawerContext | null>(null);
    const [drawerFormMode, setDrawerFormMode] = useState<'add' | 'refund' | null>(null);
    const [confirmRefundOpen, setConfirmRefundOpen] = useState(false);
    const [deletePaymentTarget, setDeletePaymentTarget] = useState<any>(null);
    const [deletePaymentReason, setDeletePaymentReason] = useState('');
    const [deletePaymentLoading, setDeletePaymentLoading] = useState(false);
    const [deletePaymentError, setDeletePaymentError] = useState('');
    const [settleTarget, setSettleTarget] = useState<any>(null);
    const [manageTarget, setManageTarget] = useState<any>(null);
    const [cancelTarget, setCancelTarget] = useState<any>(null);
    const [cancelLoading, setCancelLoading] = useState(false);
    const [cancelError, setCancelError] = useState('');
    const [corporateBranchName, setCorporateBranchName] = useState('');

    const corporateBooking = useMemo(
        () => ({
            isCorporateBooking: Boolean(booking?.isCorporateBooking),
            salesAccountId: booking?.salesAccountId ?? null,
            salesBranchId: booking?.salesBranchId ?? null,
            contactId: booking?.contactId ?? null,
            companyName: booking?.companyName || '',
            branchName: corporateBranchName,
            contactName: booking?.customerName || '',
        }),
        [booking, corporateBranchName],
    );

    useEffect(() => {
        const salesAccountId = booking?.salesAccountId;
        const salesBranchId = booking?.salesBranchId;
        if (!salesAccountId || !salesBranchId) {
            setCorporateBranchName('');
            return;
        }

        let cancelled = false;
        (async () => {
            try {
                const { data } = await axiosInstance.get(URLS.getCompanyBranchesDropdown(salesAccountId));
                const branch = (data || []).find(
                    (item: any) => String(item.id ?? item.value) === String(salesBranchId),
                );
                if (!cancelled) {
                    setCorporateBranchName(branch?.name || branch?.label || '');
                }
            } catch {
                if (!cancelled) {
                    setCorporateBranchName('');
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [booking?.salesAccountId, booking?.salesBranchId]);

    const getPaymentDetails = useCallback(async () => {
        setIsLoading(true);
        try {
            const {data} = await axiosInstance.get(URLS.getPaymentDetailsForBooking(booking.id));
            setPaymentDetails(data);
            setBookedRoomsPayments(data?.bookedRooms);
            setBookingLineItemPayments(data?.bookingLineItems);
            setSettlementEntries(data?.settlementEntries || []);
        } catch (error: any) {
            console.error('Error fetching payment details', error);
        } finally {
            setIsLoading(false);
        }
    }, [booking.id]);

    const getPaymentMethods = useCallback(async () => {
        const {status: responseStatus, data} = await axiosInstance.get(URLS.getPaymentMethods(bookingLineItem.id));
        if (responseStatus === 200) {
            const paymentMethodDropdown = createDropdownData(data, ['id', 'name']);
            setPaymentMethods(paymentMethodDropdown);
        }
    }, [bookingLineItem.id]);

    useEffect(() => {
        getPaymentDetails();
        getPaymentMethods();
    }, [getPaymentDetails, getPaymentMethods]);

    useEffect(() => {
        setDrawerContext((prev) => {
            if (!prev) return null;

            if (prev.isBookedRooms && bookedRoomsPayments) {
                return {
                    ...prev,
                    payments: bookedRoomsPayments.payments || [],
                    amountDue: bookedRoomsPayments.amountDue ?? 0,
                    amountPaid: bookedRoomsPayments.amountPaid ?? bookedRoomsPayments.paid ?? 0,
                };
            }

            const lineItem = bookingLineItemPayments?.find(
                (item: any) => Number(item.id) === Number(prev.itemId),
            );
            if (lineItem) {
                return {
                    ...prev,
                    payments: lineItem.payments || [],
                    amountDue: lineItem.amountDue ?? 0,
                    amountPaid: lineItem.amountPaid ?? lineItem.paid ?? 0,
                    itemStatus: lineItem.status || lineItem.bookingStatus || status,
                };
            }

            return prev;
        });
    }, [bookedRoomsPayments, bookingLineItemPayments, status]);

    useEffect(() => {
        setDrawerFormMode(null);
        setConfirmRefundOpen(false);
    }, [drawerContext?.rowId]);

    const onSuccesss = () => {
        onSuccess();
        getPaymentDetails();
    };

    const closeDeletePaymentDialog = () => {
        setDeletePaymentTarget(null);
        setDeletePaymentReason('');
        setDeletePaymentError('');
        setDeletePaymentLoading(false);
    };

    const handleDeletePayment = async () => {
        const reason = deletePaymentReason.trim();
        if (reason.length < 3) {
            setDeletePaymentError(strings.deleteReasonRequired);
            return;
        }
        if (!deletePaymentTarget) {
            return;
        }

        setDeletePaymentLoading(true);
        setDeletePaymentError('');
        try {
            const recordType = (deletePaymentTarget.recordType || 'PAYMENT').toUpperCase();
            await axiosInstance.delete(URLS.deletePayment(booking.id, deletePaymentTarget.id), {
                data: {reason},
                params: {recordType},
            });
            closeDeletePaymentDialog();
            onSuccesss();
        } catch (error: any) {
            setDeletePaymentError(
                error.response?.data?.error_description ||
                    error.response?.data?.message ||
                    error.message ||
                    strings.something_went_wrong,
            );
        } finally {
            setDeletePaymentLoading(false);
        }
    };

    const hasAnyCancelledLineItem = bookingLineItemPayments?.some(
        (item: any) => item.status?.toUpperCase() === 'CANCELLED',
    );

    const collectionSummary = useMemo(() => {
        const totalAmount = Number(paymentDetails?.totalAmount) || 0;
        const amountDue = Number(paymentDetails?.amountDue) || 0;
        const amountPaid = Math.max(0, totalAmount - amountDue);
        const collectionPercent =
            totalAmount > 0 ? Math.min(100, Math.round((amountPaid / totalAmount) * 100)) : 0;

        return {totalAmount, amountDue, amountPaid, collectionPercent};
    }, [paymentDetails]);

    const openPaymentDrawer = (context: PaymentDrawerContext) => {
        setDrawerContext(context);
    };

    const closePaymentDrawer = () => {
        setDrawerContext(null);
        setDrawerFormMode(null);
        setConfirmRefundOpen(false);
    };

    const canShowDrawerAddPayment = (context: PaymentDrawerContext) =>
        !(context.isBookedRooms && isYcs) &&
        (context.isBookedRooms ||
            String(context.itemStatus || status || '').toUpperCase() === 'CONFIRMED') &&
        !bookingLineItem.deleted &&
        permission.includes(privileges.acceptPayment);

    const renderPaymentHistoryTable = (payments: any[], showAddPayment = false, isAddFormOpen = false) => {
        if (!payments?.length) {
            return (
                <Paper
                    variant="outlined"
                    sx={{
                        borderRadius: 2,
                        py: 4,
                        px: 3,
                        textAlign: 'center',
                        bgcolor: 'rgba(0, 0, 0, 0.02)',
                        borderStyle: 'dashed',
                        width: '100%',
                        maxWidth: 420,
                    }}>
                    <Box
                        sx={{
                            width: 56,
                            height: 56,
                            borderRadius: '50%',
                            bgcolor: 'background.paper',
                            border: '1px solid',
                            borderColor: 'divider',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mx: 'auto',
                            mb: 1.5,
                        }}>
                        <PaymentsOutlinedIcon sx={{fontSize: 28, color: 'text.disabled'}}/>
                    </Box>
                    <Typography variant="subtitle1" sx={{fontWeight: 700, mb: 0.5}}>
                        {strings.paymentDetails_drawer_empty_title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{maxWidth: 360, mx: 'auto'}}>
                        {strings.paymentDetails_drawer_empty_subtitle}
                    </Typography>
                    {showAddPayment && !isAddFormOpen && (
                        <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            startIcon={<AddIcon/>}
                            sx={{mt: 2.5}}
                            onClick={() => setDrawerFormMode('add')}>
                            {strings.bookingDetailsAddPayment_newButton}
                        </Button>
                    )}
                </Paper>
            );
        }

        return (
            <TableContainer component={Paper} elevation={0} sx={{border: '1px solid #e0e0e0', borderRadius: 1}}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{backgroundColor: '#fafafa'}}>
                            <TableCell sx={drawerHeaderCellSx}>{strings.paymentDetails_text_date}</TableCell>
                            <TableCell align="right" sx={drawerHeaderCellSx}>{strings.paymentDetails_drawer_column_amount}</TableCell>
                            <TableCell sx={drawerHeaderCellSx}>{strings.paymentDetails_drawer_column_mode}</TableCell>
                            <TableCell sx={drawerHeaderCellSx}>{strings.paymentDetails_drawer_column_refNo}</TableCell>
                            <TableCell align="center" sx={drawerHeaderCellSx}>{strings.paymentDetails_text_attachment}</TableCell>
                            {isBbrAdmin && (
                                <TableCell align="center" sx={drawerHeaderCellSx}>
                                    {strings.paymentDetails_drawer_column_actions}
                                </TableCell>
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {(payments || []).map((payment: any) => {
                            const amountNum = Number(payment.amountPaid) || 0;
                            const amountColor = amountNum < 0 ? '#D93025' : '#188038';
                            const isRefund = (payment.recordType || '').toUpperCase() === 'REFUND';

                            return (
                                <TableRow key={`${payment.recordType || 'PAYMENT'}-${payment.id}`} sx={{'&:last-child td': {border: 0}}}>
                                    <TableCell>
                                        {formatPaymentDateTimeCell(payment.paymentDate, isRefund)}
                                        {payment.createdBy && (
                                            <Typography variant="caption" color="text.secondary" sx={{display: 'block', mt: 0.25}}>
                                                {payment.createdBy}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography sx={{color: amountColor, fontWeight: 600, fontVariantNumeric: 'tabular-nums'}}>
                                            {payment.formattedAmountPaid || Currency(payment.amountPaid) || '—'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={payment.paymentMethodName || '—'}
                                            size="small"
                                            sx={{fontWeight: 500, fontSize: 11, ...getPaymentModeChipColor()}}
                                        />
                                        <PaymentMetaDetails payment={payment}/>
                                    </TableCell>
                                    <TableCell sx={{color: '#666'}}>
                                        {payment.paymentReference || '—'}
                                    </TableCell>
                                    <TableCell align="center">
                                        {payment.attachmentUrl ? (
                                            <IconButton
                                                size="small"
                                                component={Link}
                                                href={Config.BaseURL + payment.attachmentUrl}
                                                target="_blank"
                                                download
                                                sx={{color: '#188038'}}>
                                                <DownloadIcon fontSize="small"/>
                                            </IconButton>
                                        ) : (
                                            '—'
                                        )}
                                    </TableCell>
                                    {isBbrAdmin && (
                                        <TableCell align="center">
                                            <Tooltip title={strings.paymentDetails_deletePayment_title} arrow>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => {
                                                        setDeletePaymentError('');
                                                        setDeletePaymentReason('');
                                                        setDeletePaymentTarget(payment);
                                                    }}>
                                                    <DeleteOutlineIcon fontSize="small"/>
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    )}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };

    const renderDrawerToolbar = (context: PaymentDrawerContext) => {
        const effectiveStatus = (context.itemStatus || status)?.toUpperCase();
        const showRefundForBookedRooms =
            context.isBookedRooms && context.amountPaid > 0 && hasAnyCancelledLineItem;
        const showAddPayment = canShowDrawerAddPayment(context);
        const showRefund =
            !(context.isBookedRooms && isYcs) &&
            (showRefundForBookedRooms ||
                (!context.isBookedRooms && (effectiveStatus === 'CANCELLED' || context.amountDue < 0))) &&
            !bookingLineItem.deleted &&
            permission.includes(privileges.acceptPayment);
        const addPaymentUrl = context.isBookedRooms
            ? URLS.addPaymentForBookedRooms(booking.id)
            : URLS.addPaymentForLineItem(context.itemId);
        const refundUrl = context.isBookedRooms
            ? URLS.refundPaymentForBookedRooms(booking.id)
            : URLS.refundPaymentForLineItem(context.itemId);

        const totalAmount = Number(context.amountPaid) + Number(context.amountDue);
        const dueNum = Number(context.amountDue) || 0;
        const dueColor = dueNum <= 0 ? 'text.secondary' : '#D93025';
        const lineItemCollectionPercent =
            totalAmount > 0 ? Math.min(100, Math.round((Number(context.amountPaid) / totalAmount) * 100)) : 0;
        const lineItemProgressColor = lineItemCollectionPercent >= 100 ? '#188038' : theme.palette.primary.main;

        return (
            <Box sx={{mb: 2}}>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'minmax(0, 1fr) auto',
                        alignItems: 'center',
                        columnGap: 2,
                        rowGap: 1.5,
                        width: '100%',
                    }}>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', minWidth: 0}}>
                        <Box sx={{display: 'flex', gap: 2.5, flexWrap: 'wrap'}}>
                            <Box>
                                <Typography variant="caption" color="text.secondary" sx={{display: 'block', fontSize: 10, textTransform: 'uppercase'}}>
                                    {strings.bookingDetail_summary_total}
                                </Typography>
                                <Typography variant="body2" fontWeight={700}>
                                    {Currency(totalAmount)}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary" sx={{display: 'block', fontSize: 10, textTransform: 'uppercase'}}>
                                    {strings.bookingDetail_summary_paid}
                                </Typography>
                                <Typography variant="body2" fontWeight={700} sx={{color: '#188038'}}>
                                    {Currency(context.amountPaid)}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary" sx={{display: 'block', fontSize: 10, textTransform: 'uppercase'}}>
                                    {strings.bookingDetail_summary_due}
                                </Typography>
                                <Typography variant="body2" fontWeight={700} sx={{color: dueColor}}>
                                    {Currency(context.amountDue)}
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{position: 'relative', display: 'inline-flex', flexShrink: 0}}>
                            <CircularProgress
                                variant="determinate"
                                value={100}
                                size={44}
                                thickness={4.5}
                                sx={{color: 'rgba(0, 0, 0, 0.08)'}}
                            />
                            <CircularProgress
                                variant="determinate"
                                value={lineItemCollectionPercent}
                                size={44}
                                thickness={4.5}
                                sx={{color: lineItemProgressColor, position: 'absolute', left: 0}}
                            />
                            <Box
                                sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}>
                                <Typography variant="caption" sx={{fontWeight: 700, fontSize: 10, color: lineItemProgressColor}}>
                                    {lineItemCollectionPercent}%
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            gap: 1,
                            flexShrink: 0,
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            justifySelf: 'end',
                            whiteSpace: 'nowrap',
                        }}>
                        {showRefund && (
                            <Button
                                size="small"
                                variant={drawerFormMode === 'refund' ? 'contained' : 'outlined'}
                                color="error"
                                startIcon={<ReplayIcon/>}
                                onClick={() => setConfirmRefundOpen(true)}>
                                {strings.bookingDetailsProcessRefund}
                            </Button>
                        )}
                        {showAddPayment && drawerFormMode !== 'add' && (
                            <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                startIcon={<AddIcon/>}
                                onClick={() => setDrawerFormMode('add')}>
                                {strings.bookingDetailsAddPayment_newButton}
                            </Button>
                        )}
                    </Box>
                </Box>

                {drawerFormMode === 'add' && (
                    <AddPaymentFormPanel
                        inline
                        formLayout="drawer"
                        cashLimit={bookingLineItem.docAboveCashLimit}
                        paymentMethods={paymentMethods}
                        onSuccess={onSuccesss}
                        onCancel={() => setDrawerFormMode(null)}
                        url={addPaymentUrl}
                        isYcs={context.isBookedRooms && isYcs}
                    />
                )}

                {drawerFormMode === 'refund' && (
                    <AddPaymentFormPanel
                        inline
                        formLayout="drawer"
                        isRefund
                        cashLimit={bookingLineItem.docAboveCashLimit}
                        paymentMethods={paymentMethods}
                        onSuccess={onSuccesss}
                        onCancel={() => setDrawerFormMode(null)}
                        url={refundUrl}
                        isYcs={context.isBookedRooms && isYcs}
                        title={strings.bookingDetailsProcessRefund}
                    />
                )}

                <Dialog open={confirmRefundOpen} onClose={() => setConfirmRefundOpen(false)}>
                    <DialogTitle>{strings.bookingDetailsProcessRefund}</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            You are about to process a refund. Do you want to continue?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setConfirmRefundOpen(false)}>{strings.common_button_back}</Button>
                        <Button
                            color="error"
                            variant="contained"
                            onClick={() => {
                                setConfirmRefundOpen(false);
                                setDrawerFormMode('refund');
                            }}>
                            {strings.bookingDetailsProcessRefund}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        );
    };

    const renderSummaryRow = ({
        rowId,
        itemId,
        isBookedRooms,
        type,
        eventName,
        venue,
        dateLabel,
        totalAmount,
        amountPaid,
        amountDue,
        itemStatus,
        payments,
        disableViewPayments = false,
        isCurrentItem = false,
    }: {
        rowId: string;
        itemId: number;
        isBookedRooms: boolean;
        type: PaymentRowType;
        eventName: string;
        venue: string;
        dateLabel: string;
        totalAmount: number;
        amountPaid: number;
        amountDue: number;
        itemStatus?: string;
        payments: any[];
        disableViewPayments?: boolean;
        isCurrentItem?: boolean;
    }) => {
        const paymentStatus = getPaymentCollectionStatus(totalAmount, amountPaid, amountDue);
        const dueNum = Number(amountDue) || 0;
        const dueColor = dueNum <= 0 ? 'text.secondary' : '#D93025';
        const LineItemIcon = isBookedRooms ? HotelOutlinedIcon : CalendarMonthOutlinedIcon;
        const isHighlighted = isCurrentItem === true;

        return (
            <TableRow
                key={rowId}
                sx={{
                    ...(isHighlighted && {
                        bgcolor: 'rgba(25, 103, 210, 0.06)',
                        boxShadow: (t) => `inset 3px 0 0 ${t.palette.primary.main}`,
                    }),
                    '&:hover': {
                        bgcolor: isHighlighted ? 'rgba(25, 103, 210, 0.1)' : 'rgba(0, 0, 0, 0.02)',
                    },
                }}>
                <TableCell>
                    <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 1.5}}>
                        <LineItemIcon
                            sx={{
                                color: isHighlighted ? 'primary.main' : 'text.secondary',
                                fontSize: 22,
                                mt: 0.25,
                                flexShrink: 0,
                            }}
                        />
                        <Box sx={{minWidth: 0}}>
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap'}}>
                                <Typography
                                    variant="body2"
                                    fontWeight={600}
                                    sx={{lineHeight: 1.4, color: isHighlighted ? 'primary.main' : 'text.primary'}}>
                                    {eventName}
                                </Typography>
                                {isHighlighted && (
                                    <Chip
                                        label={strings.paymentDetails_currentItem_label}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                        sx={{height: 20, fontSize: 10, fontWeight: 700}}
                                    />
                                )}
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{lineHeight: 1.4}}>
                                {venue}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{lineHeight: 1.4}}>
                                {dateLabel}
                            </Typography>
                        </Box>
                    </Box>
                </TableCell>
                <TableCell>
                    <Chip
                        label={type === 'EVENT' ? strings.paymentDetails_type_event : strings.paymentDetails_type_bookedRooms}
                        size="small"
                        sx={type === 'EVENT' ? EVENT_TYPE_CHIP_SX : BOOKED_ROOMS_TYPE_CHIP_SX}
                    />
                </TableCell>
                <TableCell align="right">
                    <Typography fontWeight={500}>{Currency(totalAmount)}</Typography>
                </TableCell>
                <TableCell align="right">
                    <Typography sx={{color: '#188038', fontWeight: 600}}>{Currency(amountPaid)}</Typography>
                </TableCell>
                <TableCell align="right">
                    <Typography sx={{color: dueColor, fontWeight: dueNum <= 0 ? 500 : 600}}>
                        {Currency(amountDue)}
                    </Typography>
                </TableCell>
                <TableCell align="center">
                    <PaymentStatusIndicator status={paymentStatus} isComplementary={isComplementaryBooking} />
                </TableCell>
                <TableCell align="center">
                    {disableViewPayments ? (
                        <Typography variant="caption" color="text.secondary" sx={{fontStyle: 'italic'}}>
                            {strings.paymentDetails_text_ycsRoomsManaged}
                        </Typography>
                    ) : (
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() =>
                                openPaymentDrawer({
                                    rowId,
                                    itemId,
                                    isBookedRooms,
                                    eventName,
                                    venue,
                                    dateLabel,
                                    payments,
                                    amountDue,
                                    amountPaid,
                                    itemStatus,
                                })
                            }>
                            {strings.paymentDetails_button_viewPayments}
                        </Button>
                    )}
                </TableCell>
            </TableRow>
        );
    };

    const headerCellSx = {
        fontWeight: 600,
        color: '#666',
        textTransform: 'uppercase',
        fontSize: 12,
        whiteSpace: 'nowrap',
    } as const;

    const canAddReceivable =
        !bookingLineItem.deleted && permission.includes(privileges.acceptPayment);

    const canManageLedgers =
        !bookingLineItem.deleted && permission.includes(privileges.manageLedgers);

    const settlementPaymentMethods = useMemo(
        () =>
            paymentMethods.filter(
                (method: any) =>
                    !['BILL_TO_COMPANY', 'BTC', 'CITY_LEDGER', 'CL'].includes(
                        String(method.value || '').toUpperCase(),
                    ),
            ),
        [paymentMethods],
    );

    const canSettleReceivable = (entry: any) => {
        const pendingNum = Number(entry?.pendingAmount) || 0;
        const entryStatus = String(entry?.status || '').toUpperCase();
        return (
            canManageLedgers &&
            pendingNum > 0 &&
            entryStatus !== 'SETTLED' &&
            entryStatus !== 'CANCELLED'
        );
    };

    const maxReceivableAmount = useMemo(() => {
        const bookingDue = Number(paymentDetails?.amountDue) || 0;
        const pendingReceivables = (settlementEntries || []).reduce((sum: number, entry: any) => {
            const status = String(entry?.status || '').toUpperCase();
            if (status === 'SETTLED' || status === 'CANCELLED') {
                return sum;
            }
            return sum + (Number(entry?.pendingAmount) || 0);
        }, 0);
        return Math.max(0, bookingDue - pendingReceivables);
    }, [paymentDetails?.amountDue, settlementEntries]);

    const hasActiveReceipts = (entry: any) =>
        (entry?.receipts || []).some((receipt: any) => !receipt.reversed);

    const handleCancelReceivable = async () => {
        if (!cancelTarget) return;
        setCancelLoading(true);
        setCancelError('');
        try {
            await axiosInstance.post(URLS.cancelSettlementEntry(booking.id, cancelTarget.id));
            setCancelTarget(null);
            onSuccesss();
        } catch (error: any) {
            setCancelError(
                error.response?.data?.error_description ||
                    error.response?.data?.message ||
                    strings.something_went_wrong,
            );
        } finally {
            setCancelLoading(false);
        }
    };

    const collectionProgressColor =
        collectionSummary.collectionPercent >= 100 ? '#188038' : theme.palette.primary.main;

    const btcLedgerSummary = useMemo(() => {
        const activeEntries = (settlementEntries || []).filter((e: any) => String(e?.status || '').toUpperCase() !== 'CANCELLED');
        const totalAmount = activeEntries.reduce((sum: number, e: any) => sum + (Number(e?.amount) || 0), 0);
        const receivedAmount = activeEntries.reduce((sum: number, e: any) => sum + (Number(e?.receivedAmount) || 0), 0);
        const pendingAmount = activeEntries.reduce((sum: number, e: any) => sum + (Number(e?.pendingAmount) || 0), 0);
        const collectionPercent = totalAmount > 0 ? Math.min(100, Math.round((receivedAmount / totalAmount) * 100)) : 0;
        return {totalAmount, receivedAmount, pendingAmount, collectionPercent};
    }, [settlementEntries]);

    return (
        <>
            {isLoading && <PageLoader/>}

            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 2,
                    mb: 2,
                    flexWrap: 'wrap',
                }}>
                <Box sx={{flex: 1, minWidth: 220}}>
                    <Typography variant="h6" sx={{fontWeight: 700, lineHeight: 1.3}}>
                        {strings.paymentDetails_summary_title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{mt: 0.25, lineHeight: 1.4}}>
                        {strings.paymentDetails_summary_subtitle}
                    </Typography>
                    {paymentDetails.paymentOverdue && (
                        <Chip
                            size="small"
                            label={strings.paymentDetails_text_paymentOverdue}
                            color="warning"
                            sx={{mt: 1}}
                        />
                    )}
                </Box>

                <Paper
                    variant="outlined"
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 1.25,
                        px: 1.5,
                        py: 1,
                        borderRadius: 2,
                        flexShrink: 0,
                        minWidth: 0,
                    }}>
                    <Box sx={{minWidth: 0}}>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                                display: 'block',
                                fontWeight: 700,
                                fontSize: 10,
                                letterSpacing: 0.6,
                                textTransform: 'uppercase',
                                lineHeight: 1.2,
                                whiteSpace: 'nowrap',
                            }}>
                            {strings.paymentDetails_collectionPercent_label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{fontSize: 11, lineHeight: 1.3}}>
                            {Currency(collectionSummary.amountPaid)}{' '}
                            <Box component="span" sx={{opacity: 0.6}}>
                                / {Currency(collectionSummary.totalAmount)}
                            </Box>
                        </Typography>
                    </Box>

                    <Box sx={{position: 'relative', display: 'inline-flex', flexShrink: 0}}>
                        <CircularProgress
                            variant="determinate"
                            value={100}
                            size={48}
                            thickness={4.5}
                            sx={{color: 'rgba(0, 0, 0, 0.08)'}}
                        />
                        <CircularProgress
                            variant="determinate"
                            value={collectionSummary.collectionPercent}
                            size={48}
                            thickness={4.5}
                            sx={{
                                color: collectionProgressColor,
                                position: 'absolute',
                                left: 0,
                            }}
                        />
                        <Box
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}>
                            <Typography
                                variant="caption"
                                sx={{
                                    fontWeight: 700,
                                    fontSize: 11,
                                    color: collectionProgressColor,
                                    fontVariantNumeric: 'tabular-nums',
                                }}>
                                {collectionSummary.collectionPercent}%
                            </Typography>
                        </Box>
                    </Box>
                </Paper>
            </Box>

            <TableContainer component={Paper} elevation={0} sx={{border: '1px solid #e0e0e0', borderRadius: 2}}>
                <Table>
                    <TableHead>
                        <TableRow sx={{backgroundColor: '#fafafa'}}>
                            <TableCell sx={headerCellSx}>{strings.paymentDetails_column_itemLineItem}</TableCell>
                            <TableCell sx={headerCellSx}>{strings.paymentDetails_column_type}</TableCell>
                            <TableCell align="right" sx={headerCellSx}>{strings.paymentDetails_column_totalAmount}</TableCell>
                            <TableCell align="right" sx={headerCellSx}>{strings.paymentDetails_column_paidAmount}</TableCell>
                            <TableCell align="right" sx={headerCellSx}>{strings.paymentDetails_column_dueAmount}</TableCell>
                            <TableCell align="center" sx={headerCellSx}>{strings.paymentDetails_column_status}</TableCell>
                            <TableCell align="center" sx={headerCellSx}>{strings.paymentDetails_column_actions}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {bookedRoomsPayments &&
                            renderSummaryRow({
                                rowId: 'bookedRooms',
                                itemId: booking.id,
                                isBookedRooms: true,
                                type: 'BOOKED_ROOMS',
                                eventName: strings.paymentDetails_bookedRooms_title,
                                venue: bookedRoomsPayments.roomCount
                                    ? `${bookedRoomsPayments.roomCount} Room(s)`
                                    : strings.paymentDetails_bookedRooms_subtitle,
                                dateLabel: formatPaymentDate(bookedRoomsPayments.startDate),
                                totalAmount: bookedRoomsPayments.totalAmount ?? bookedRoomsPayments.total ?? 0,
                                amountPaid: bookedRoomsPayments.amountPaid ?? bookedRoomsPayments.paid ?? 0,
                                amountDue: bookedRoomsPayments.amountDue ?? 0,
                                payments: bookedRoomsPayments.payments || [],
                                disableViewPayments: isYcs,
                            })}

                        {bookingLineItemPayments?.map((paymentLineItem: any) =>
                            renderSummaryRow({
                                rowId: `lineItem-${paymentLineItem.id}`,
                                itemId: paymentLineItem.id,
                                isBookedRooms: false,
                                type: 'EVENT',
                                eventName: paymentLineItem.eventType || '—',
                                venue: paymentLineItem.venue || '—',
                                dateLabel: formatPaymentDate(paymentLineItem.startDate),
                                totalAmount: paymentLineItem.totalAmount ?? paymentLineItem.total ?? 0,
                                amountPaid: paymentLineItem.amountPaid ?? paymentLineItem.paid ?? 0,
                                amountDue: paymentLineItem.amountDue ?? 0,
                                itemStatus: paymentLineItem.status || paymentLineItem.bookingStatus || status,
                                payments: paymentLineItem.payments || [],
                                isCurrentItem: Number(paymentLineItem.id) === Number(bookingLineItem.id),
                            }),
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box sx={{mt: 3}}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 2,
                        mb: 1.5,
                        flexWrap: 'wrap',
                    }}>
                    <Typography variant="h6" sx={{fontWeight: 700, lineHeight: 1.3}}>
                        {strings.receivable_section_title}
                    </Typography>

                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap'}}>
                        {settlementEntries?.length > 0 && btcLedgerSummary.totalAmount > 0 && (
                            <Paper
                                variant="outlined"
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 1.25,
                                    px: 1.5,
                                    py: 1,
                                    borderRadius: 2,
                                    flexShrink: 0,
                                    minWidth: 0,
                                }}>
                                <Box sx={{minWidth: 0}}>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{
                                            display: 'block',
                                            fontWeight: 700,
                                            fontSize: 10,
                                            letterSpacing: 0.6,
                                            textTransform: 'uppercase',
                                            lineHeight: 1.2,
                                            whiteSpace: 'nowrap',
                                        }}>
                                        {strings.receivable_cancel_summaryLabel}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{fontSize: 11, lineHeight: 1.3}}>
                                        {Currency(btcLedgerSummary.receivedAmount)}{' '}
                                        <Box component="span" sx={{opacity: 0.6}}>
                                            / {Currency(btcLedgerSummary.totalAmount)}
                                        </Box>
                                    </Typography>
                                    <Typography variant="caption" sx={{display: 'block', fontSize: 10, color: btcLedgerSummary.pendingAmount > 0 ? '#D93025' : '#188038', fontWeight: 600}}>
                                        {btcLedgerSummary.pendingAmount > 0 ? `${Currency(btcLedgerSummary.pendingAmount)} pending` : 'Fully received'}
                                    </Typography>
                                </Box>
                                <Box sx={{position: 'relative', display: 'inline-flex', flexShrink: 0}}>
                                    <CircularProgress
                                        variant="determinate"
                                        value={100}
                                        size={48}
                                        thickness={4.5}
                                        sx={{color: 'rgba(0, 0, 0, 0.08)'}}
                                    />
                                    <CircularProgress
                                        variant="determinate"
                                        value={btcLedgerSummary.collectionPercent}
                                        size={48}
                                        thickness={4.5}
                                        sx={{
                                            color: btcLedgerSummary.collectionPercent >= 100 ? '#188038' : theme.palette.primary.main,
                                            position: 'absolute',
                                            left: 0,
                                        }}
                                    />
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            inset: 0,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                fontWeight: 700,
                                                fontSize: 11,
                                                color: btcLedgerSummary.collectionPercent >= 100 ? '#188038' : theme.palette.primary.main,
                                                fontVariantNumeric: 'tabular-nums',
                                            }}>
                                            {btcLedgerSummary.collectionPercent}%
                                        </Typography>
                                    </Box>
                                </Box>
                            </Paper>
                        )}

                        {canAddReceivable && (
                            <AddReceivableButton
                                bookingId={booking.id}
                                onSuccess={onSuccesss}
                                maxReceivableAmount={maxReceivableAmount}
                                defaultCompanyName={booking.companyName}
                                defaultGstNumber={booking.gst}
                                corporateBooking={corporateBooking}
                            />
                        )}
                    </Box>
                </Box>

            {settlementEntries?.length > 0 && (
                <TableContainer component={Paper} elevation={0} sx={{border: '1px solid #e0e0e0', borderRadius: 2}}>
                    <Table size="small" sx={{'& .MuiTableCell-root': {py: 1, px: 1.5, fontSize: '0.8125rem'}}}>
                        <TableHead>
                            <TableRow sx={{backgroundColor: '#fafafa'}}>
                                <TableCell sx={{...headerCellSx, minWidth: 110}}>{strings.receivable_column_date}</TableCell>
                                <TableCell sx={{...headerCellSx, minWidth: 120}}>{strings.receivable_column_party}</TableCell>
                                <TableCell align="right" sx={{...headerCellSx, whiteSpace: 'nowrap'}}>{strings.receivable_column_amount}</TableCell>
                                <TableCell align="right" sx={{...headerCellSx, whiteSpace: 'nowrap'}}>{strings.receivable_column_received}</TableCell>
                                <TableCell align="right" sx={{...headerCellSx, whiteSpace: 'nowrap'}}>{strings.receivable_column_pending}</TableCell>
                                <TableCell align="center" sx={{...headerCellSx, minWidth: 72}}>{strings.receivable_column_status}</TableCell>
                                <TableCell align="center" sx={{...headerCellSx, width: 96}}>{strings.paymentDetails_column_actions}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {settlementEntries.map((entry: any) => {
                                const pendingNum = Number(entry.pendingAmount) || 0;
                                const pendingColor = pendingNum <= 0 ? 'text.secondary' : '#D93025';
                                return (
                                    <TableRow
                                        key={`settlement-${entry.id}`}
                                        sx={{'&:hover': {bgcolor: 'rgba(0, 0, 0, 0.02)'}, '&:last-child td': {border: 0}}}>
                                        {/* Date + type chip + added by */}
                                        <TableCell sx={{verticalAlign: 'top'}}>
                                            <Typography variant="body2" sx={{fontWeight: 600, fontSize: '0.8125rem', lineHeight: 1.3}}>
                                                {formatPaymentDate(entry.createdAt)}
                                            </Typography>
                                            <Chip
                                                label={getReceivableTypeLabel(entry.type)}
                                                size="small"
                                                sx={{...RECEIVABLE_TYPE_CHIP_SX, mt: 0.5, height: 18, fontSize: 10}}
                                            />
                                            {entry.createdBy && (
                                                <Typography variant="caption" color="text.secondary" sx={{display: 'block', mt: 0.25, fontSize: 11}}>
                                                    {entry.createdBy}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        {/* Party */}
                                        <TableCell sx={{verticalAlign: 'top'}}>
                                            <Typography variant="body2" sx={{fontWeight: 500, fontSize: '0.8125rem', lineHeight: 1.3}}>
                                                {entry.companyName || '—'}
                                            </Typography>
                                            {entry.remarks && (
                                                <Tooltip title={entry.remarks} arrow placement="top">
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{
                                                            display: 'block',
                                                            mt: 0.25,
                                                            fontSize: 11,
                                                            maxWidth: 140,
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                        }}>
                                                        {entry.remarks}
                                                    </Typography>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                        <TableCell align="right" sx={{verticalAlign: 'top', whiteSpace: 'nowrap'}}>
                                            <Typography variant="body2" sx={{fontWeight: 600, fontVariantNumeric: 'tabular-nums', fontSize: '0.8125rem'}}>
                                                {entry.formattedAmount || Currency(entry.amount)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right" sx={{verticalAlign: 'top', whiteSpace: 'nowrap'}}>
                                            <Typography variant="body2" sx={{color: '#188038', fontWeight: 600, fontVariantNumeric: 'tabular-nums', fontSize: '0.8125rem'}}>
                                                {entry.formattedReceivedAmount || Currency(entry.receivedAmount)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right" sx={{verticalAlign: 'top', whiteSpace: 'nowrap'}}>
                                            <Typography variant="body2" sx={{color: pendingColor, fontWeight: pendingNum <= 0 ? 500 : 600, fontVariantNumeric: 'tabular-nums', fontSize: '0.8125rem'}}>
                                                {entry.formattedPendingAmount || Currency(entry.pendingAmount)}
                                            </Typography>
                                        </TableCell>
                                        {/* Status */}
                                        <TableCell align="center" sx={{verticalAlign: 'top'}}>
                                            <ReceivableStatusIndicator status={entry.status as SettlementEntryStatus} />
                                        </TableCell>
                                        {/* Actions: attachment + settle/manage/cancel */}
                                        <TableCell align="center" sx={{verticalAlign: 'top'}}>
                                            <Box sx={{display: 'flex', gap: 0, justifyContent: 'center', alignItems: 'center'}}>
                                                {entry.attachmentUrl && (
                                                    <Tooltip title={strings.paymentDetails_text_attachment} arrow>
                                                        <IconButton
                                                            size="small"
                                                            component={Link}
                                                            href={Config.BaseURL + entry.attachmentUrl}
                                                            target="_blank"
                                                            download
                                                            sx={{color: '#188038', p: 0.5}}>
                                                            <DownloadIcon sx={{fontSize: 17}}/>
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {canSettleReceivable(entry) && (
                                                    <Tooltip title={strings.receivable_button_settle} arrow>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => setSettleTarget(entry)}
                                                            sx={{color: '#188038', p: 0.5}}>
                                                            <CheckCircleOutlineIcon sx={{fontSize: 17}}/>
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {hasActiveReceipts(entry) && canManageLedgers && (
                                                    <Tooltip title={strings.receivable_button_manage} arrow>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => setManageTarget(entry)}
                                                            sx={{color: 'warning.main', p: 0.5}}>
                                                            <ReplayIcon sx={{fontSize: 17}}/>
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {entry.canCancel && canAddReceivable && (
                                                    <Tooltip title={strings.receivable_button_cancel} arrow>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => {
                                                                setCancelError('');
                                                                setCancelTarget(entry);
                                                            }}
                                                            sx={{color: 'error.main', p: 0.5}}>
                                                            <BlockOutlinedIcon sx={{fontSize: 17}}/>
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
            </Box>

            <SettleReceivableDrawer
                open={Boolean(settleTarget)}
                onClose={() => setSettleTarget(null)}
                bookingId={booking.id}
                settlementEntry={settleTarget}
                paymentMethods={settlementPaymentMethods}
                onSuccess={onSuccesss}
            />

            <ManageReceivableDrawer
                open={Boolean(manageTarget)}
                onClose={() => setManageTarget(null)}
                bookingId={booking.id}
                settlementEntry={manageTarget}
                onSuccess={onSuccesss}
            />

            <Dialog
                open={Boolean(cancelTarget)}
                onClose={() => !cancelLoading && setCancelTarget(null)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        overflow: 'hidden',
                        boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)',
                    },
                }}>
                {cancelTarget && (
                    <>
                        <Box
                            sx={{
                                px: 3,
                                py: 2.5,
                                bgcolor: 'rgba(211, 47, 47, 0.06)',
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                            }}>
                            <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 2}}>
                                <Box
                                    sx={{
                                        width: 48,
                                        height: 48,
                                        borderRadius: 2,
                                        bgcolor: 'error.main',
                                        color: 'error.contrastText',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                        boxShadow: '0 4px 12px rgba(211, 47, 47, 0.28)',
                                    }}>
                                    <WarningAmberOutlinedIcon/>
                                </Box>
                                <Box sx={{minWidth: 0, pt: 0.25}}>
                                    <Typography variant="h6" sx={{fontWeight: 700, lineHeight: 1.3, mb: 0.5}}>
                                        {strings.receivable_cancel_title}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{lineHeight: 1.5}}>
                                        {strings.receivable_cancel_prompt}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        <DialogContent sx={{px: 3, pt: 2, pb: 1}}>
                            <Typography
                                variant="caption"
                                sx={{
                                    display: 'block',
                                    color: 'text.secondary',
                                    fontWeight: 600,
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.6,
                                    mb: 0.75,
                                }}>
                                {strings.receivable_cancel_summaryLabel}
                            </Typography>
                            <Paper
                                variant="outlined"
                                sx={{
                                    px: 1.5,
                                    py: 1,
                                    mb: cancelError ? 2 : 0,
                                    borderRadius: 1.5,
                                    bgcolor: 'rgba(0, 0, 0, 0.02)',
                                    borderColor: 'divider',
                                }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        alignItems: 'center',
                                        columnGap: 1.5,
                                        rowGap: 0.75,
                                    }}>
                                    <Box sx={{display: 'inline-flex', alignItems: 'center', gap: 0.5}}>
                                        <Typography variant="caption" color="text.secondary">
                                            {strings.receivable_column_type}:
                                        </Typography>
                                        <Chip
                                            label={getReceivableTypeLabel(cancelTarget.type)}
                                            size="small"
                                            sx={{height: 22, ...RECEIVABLE_TYPE_CHIP_SX}}
                                        />
                                    </Box>
                                    <Divider orientation="vertical" flexItem sx={{display: {xs: 'none', sm: 'block'}, my: 0.25}}/>
                                    <Box sx={{display: 'inline-flex', alignItems: 'baseline', gap: 0.5, minWidth: 0}}>
                                        <Typography variant="caption" color="text.secondary" sx={{flexShrink: 0}}>
                                            {strings.receivable_column_party}:
                                        </Typography>
                                        <Typography variant="body2" fontWeight={500} noWrap sx={{maxWidth: 160}}>
                                            {cancelTarget.companyName || '—'}
                                        </Typography>
                                    </Box>
                                    <Divider orientation="vertical" flexItem sx={{display: {xs: 'none', sm: 'block'}, my: 0.25}}/>
                                    <Box sx={{display: 'inline-flex', alignItems: 'center', gap: 0.5}}>
                                        <Typography variant="caption" color="text.secondary">
                                            {strings.receivable_column_amount}:
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{fontWeight: 700, fontVariantNumeric: 'tabular-nums'}}>
                                            {cancelTarget.formattedAmount || Currency(cancelTarget.amount)}
                                        </Typography>
                                    </Box>
                                    <Divider orientation="vertical" flexItem sx={{display: {xs: 'none', sm: 'block'}, my: 0.25}}/>
                                    <Box sx={{display: 'inline-flex', alignItems: 'baseline', gap: 0.5}}>
                                        <Typography variant="caption" color="text.secondary">
                                            {strings.receivable_column_date}:
                                        </Typography>
                                        <Typography variant="body2" fontWeight={500} sx={{whiteSpace: 'nowrap'}}>
                                            {formatPaymentDate(cancelTarget.createdAt)}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Paper>
                            {cancelError && (
                                <Typography variant="body2" color="error" sx={{mt: 1.5}}>
                                    {cancelError}
                                </Typography>
                            )}
                        </DialogContent>

                        <Divider/>

                        <DialogActions
                            sx={{
                                px: 3,
                                py: 2,
                                bgcolor: 'rgba(0, 0, 0, 0.02)',
                                gap: 1,
                            }}>
                            <Button
                                variant="outlined"
                                onClick={() => setCancelTarget(null)}
                                disabled={cancelLoading}
                                sx={{minWidth: 100, borderRadius: 2}}>
                                {strings.common_button_back}
                            </Button>
                            <Button
                                color="error"
                                variant="contained"
                                onClick={handleCancelReceivable}
                                disabled={cancelLoading}
                                startIcon={
                                    cancelLoading ? (
                                        <CircularProgress size={18} color="inherit"/>
                                    ) : (
                                        <WarningAmberOutlinedIcon/>
                                    )
                                }
                                sx={{
                                    minWidth: 148,
                                    borderRadius: 2,
                                    boxShadow: '0 4px 12px rgba(211, 47, 47, 0.24)',
                                }}>
                                {strings.receivable_cancel_confirm}
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            <DrawerMenu
                open={Boolean(drawerContext)}
                handleClose={closePaymentDrawer}
                closeButton
                flexColumn
                width={screenSizeDownSm ? '100%' : 560}
                title={
                    drawerContext ? (
                        <Box>
                            <Typography fontWeight={600} fontSize="18px">
                                {drawerContext.eventName}
                            </Typography>
                            <Typography variant="body2" sx={{opacity: 0.9}}>
                                {drawerContext.venue}
                            </Typography>
                            <Typography variant="caption" sx={{opacity: 0.85}}>
                                {drawerContext.dateLabel}
                            </Typography>
                        </Box>
                    ) : (
                        strings.paymentDetails_drawer_title
                    )
                }>
                {drawerContext && (
                    <Box sx={{display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, width: '100%'}}>
                        {renderDrawerToolbar(drawerContext)}
                        <Box
                            sx={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                minHeight: 0,
                                width: '100%',
                                ...(!drawerContext.payments?.length && {
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }),
                            }}>
                            {renderPaymentHistoryTable(
                                drawerContext.payments,
                                canShowDrawerAddPayment(drawerContext),
                                drawerFormMode === 'add',
                            )}
                        </Box>
                    </Box>
                )}
            </DrawerMenu>

            <Dialog
                open={Boolean(deletePaymentTarget)}
                onClose={closeDeletePaymentDialog}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        overflow: 'hidden',
                        boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)',
                    },
                }}>
                {deletePaymentTarget && (() => {
                    const isRefund = (deletePaymentTarget.recordType || 'PAYMENT').toUpperCase() === 'REFUND';
                    const amountNum = Number(deletePaymentTarget.amountPaid) || 0;
                    const amountColor = amountNum < 0 ? '#D93025' : '#188038';
                    const dialogTitle = isRefund
                        ? strings.paymentDetails_deleteRefund_title
                        : strings.paymentDetails_deletePayment_title;
                    const dialogPrompt = isRefund
                        ? strings.paymentDetails_deleteRefund_prompt
                        : strings.paymentDetails_deletePayment_prompt;
                    const confirmLabel = isRefund
                        ? strings.paymentDetails_deleteRefund_confirm
                        : strings.paymentDetails_deletePayment_confirm;
                    const reasonLength = deletePaymentReason.trim().length;
                    const reasonTooShort = reasonLength > 0 && reasonLength < 3;

                    return (
                        <>
                            <Box
                                sx={{
                                    px: 3,
                                    py: 2.5,
                                    bgcolor: 'rgba(211, 47, 47, 0.06)',
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                }}>
                                <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 2}}>
                                    <Box
                                        sx={{
                                            width: 48,
                                            height: 48,
                                            borderRadius: 2,
                                            bgcolor: 'error.main',
                                            color: 'error.contrastText',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0,
                                            boxShadow: '0 4px 12px rgba(211, 47, 47, 0.28)',
                                        }}>
                                        <DeleteOutlineIcon/>
                                    </Box>
                                    <Box sx={{minWidth: 0, pt: 0.25}}>
                                        <Typography variant="h6" sx={{fontWeight: 700, lineHeight: 1.3, mb: 0.5}}>
                                            {dialogTitle}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{lineHeight: 1.5}}>
                                            {dialogPrompt}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>

                            <DialogContent sx={{px: 3, pt: 2, pb: 1}}>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        display: 'block',
                                        color: 'text.secondary',
                                        fontWeight: 600,
                                        textTransform: 'uppercase',
                                        letterSpacing: 0.6,
                                        mb: 0.75,
                                    }}>
                                    {strings.paymentDetails_deletePayment_summaryLabel}
                                </Typography>
                                <Paper
                                    variant="outlined"
                                    sx={{
                                        px: 1.5,
                                        py: 1,
                                        mb: 2,
                                        borderRadius: 1.5,
                                        bgcolor: 'rgba(0, 0, 0, 0.02)',
                                        borderColor: 'divider',
                                    }}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            alignItems: 'center',
                                            columnGap: 1.5,
                                            rowGap: 0.75,
                                        }}>
                                        <Box sx={{display: 'inline-flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap'}}>
                                            <Typography variant="caption" color="text.secondary">
                                                {strings.paymentDetails_drawer_column_amount}:
                                            </Typography>
                                            {isRefund && (
                                                <Chip
                                                    label={strings.paymentDetails_type_refund}
                                                    size="small"
                                                    sx={{height: 22, ...REFUND_TYPE_CHIP_SX}}
                                                />
                                            )}
                                            <Typography
                                                variant="body2"
                                                sx={{fontWeight: 700, color: amountColor, fontVariantNumeric: 'tabular-nums'}}>
                                                {deletePaymentTarget.formattedAmountPaid ||
                                                    Currency(deletePaymentTarget.amountPaid) ||
                                                    '—'}
                                            </Typography>
                                        </Box>
                                        <Divider orientation="vertical" flexItem sx={{display: {xs: 'none', sm: 'block'}, my: 0.25}}/>
                                        <Box sx={{display: 'inline-flex', alignItems: 'center', gap: 0.5}}>
                                            <Typography variant="caption" color="text.secondary">
                                                {strings.paymentDetails_drawer_column_mode}:
                                            </Typography>
                                            <Chip
                                                label={deletePaymentTarget.paymentMethodName || '—'}
                                                size="small"
                                                sx={{height: 22, fontWeight: 600, fontSize: 10, ...getPaymentModeChipColor()}}
                                            />
                                        </Box>
                                        <Divider orientation="vertical" flexItem sx={{display: {xs: 'none', sm: 'block'}, my: 0.25}}/>
                                        <Box sx={{display: 'inline-flex', alignItems: 'baseline', gap: 0.5, minWidth: 0}}>
                                            <Typography variant="caption" color="text.secondary" sx={{flexShrink: 0}}>
                                                {strings.paymentDetails_drawer_column_refNo}:
                                            </Typography>
                                            <Typography variant="body2" fontWeight={500} noWrap sx={{maxWidth: 140}}>
                                                {deletePaymentTarget.paymentReference || '—'}
                                            </Typography>
                                        </Box>
                                        <Divider orientation="vertical" flexItem sx={{display: {xs: 'none', sm: 'block'}, my: 0.25}}/>
                                        <Box sx={{display: 'inline-flex', alignItems: 'baseline', gap: 0.5}}>
                                            <Typography variant="caption" color="text.secondary">
                                                {strings.paymentDetails_text_date}:
                                            </Typography>
                                            <Typography variant="body2" fontWeight={500} sx={{whiteSpace: 'nowrap'}}>
                                                {deletePaymentTarget.paymentDate
                                                    ? dayjs(deletePaymentTarget.paymentDate).format('D MMM YYYY, h:mm A')
                                                    : '—'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Paper>

                                <Typography variant="subtitle2" fontWeight={700} sx={{mb: 1}}>
                                    {strings.paymentDetails_deletePayment_reasonLabel}
                                    <Typography component="span" color="error.main">
                                        {' *'}
                                    </Typography>
                                </Typography>
                                <TextField
                                    autoFocus
                                    required
                                    fullWidth
                                    multiline
                                    minRows={3}
                                    maxRows={5}
                                    value={deletePaymentReason}
                                    onChange={(e) => {
                                        setDeletePaymentReason(e.target.value);
                                        if (deletePaymentError) {
                                            setDeletePaymentError('');
                                        }
                                    }}
                                    placeholder={strings.paymentDetails_deletePayment_reasonPlaceholder}
                                    error={Boolean(deletePaymentError) || reasonTooShort}
                                    helperText={
                                        deletePaymentError ||
                                        (reasonTooShort ? strings.deleteReasonRequired : `${reasonLength}/500 characters`)
                                    }
                                    inputProps={{maxLength: 500}}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 2,
                                            bgcolor: 'background.paper',
                                        },
                                    }}
                                />
                            </DialogContent>

                            <Divider/>

                            <DialogActions
                                sx={{
                                    px: 3,
                                    py: 2,
                                    bgcolor: 'rgba(0, 0, 0, 0.02)',
                                    gap: 1,
                                }}>
                                <Button
                                    variant="outlined"
                                    onClick={closeDeletePaymentDialog}
                                    disabled={deletePaymentLoading}
                                    sx={{minWidth: 100, borderRadius: 2}}>
                                    {strings.common_button_back}
                                </Button>
                                <Button
                                    color="error"
                                    variant="contained"
                                    disabled={deletePaymentLoading || reasonLength < 3}
                                    onClick={handleDeletePayment}
                                    startIcon={
                                        deletePaymentLoading ? (
                                            <CircularProgress size={18} color="inherit"/>
                                        ) : (
                                            <DeleteOutlineIcon/>
                                        )
                                    }
                                    sx={{
                                        minWidth: 148,
                                        borderRadius: 2,
                                        boxShadow: '0 4px 12px rgba(211, 47, 47, 0.24)',
                                    }}>
                                    {confirmLabel}
                                </Button>
                            </DialogActions>
                        </>
                    );
                })()}
            </Dialog>
        </>
    );
};

export default PaymentDetails;
